import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { IAccountResponse, IStepTemplateResponse } from "@miplatform/types";
import { getHoursAndMinutes } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

const TOTAL_MINUTES_PER_DAY = 480;

const formatDuration = (durationInMinutes: number): string => {
  const { hours, minutes } = getHoursAndMinutes(durationInMinutes);

  const hoursPart = hours ? `${hours} hour${hours > 1 ? "s" : ""}` : "";
  const seperator = hours && minutes ? ", " : "";
  const minutesPart = minutes
    ? `${minutes} minute${minutes > 1 ? "s" : ""}`
    : "";

  return `${hoursPart}${seperator}${minutesPart}` || "--";
};

export const ConfigWorkOrderCustomerCard = ({
  account,
  steps = [],
  disabled = false,
  isExcluded = false,
  onClickExclude,
  onClickReenable,
}: {
  account: IAccountResponse;
  steps?: IStepTemplateResponse[];
  disabled?: boolean;
  isExcluded?: boolean;
  onClickExclude: () => void;
  onClickReenable: () => void;
}) => {
  const { classes, theme } = useStyles({ isExcluded });

  const stepDurations = useMemo(() => {
    const durations: Record<string, number> = {};
    steps.forEach((step) => {
      const customDuration = step?.builderDurations?.find(
        (bd) => bd.builderId === account.id
      )?.duration;
      durations[step.id] = customDuration ?? step?.duration ?? 0;
    });
    return durations;
  }, [steps, account.id]);

  const totalDuration = steps.reduce(
    (total, step) => total + (stepDurations[step.id] ?? 0),
    0
  );
  const capacityPerDay =
    totalDuration > 0
      ? Math.floor(TOTAL_MINUTES_PER_DAY / totalDuration)
      : "N/A";

  return (
    <Box className={classes.root}>
      <Box className={classes.cardContent}>
        <Box className={classes.cardTitle}>
          <Typography
            variant="HD2"
            color={
              !isExcluded
                ? theme.palette.primary.main
                : theme.palette.common.black
            }
            component="div"
            sx={{ flexGrow: 1 }}
          >
            {account.name}
          </Typography>
          {steps.length > 0 && (
            <Typography
              variant="HD2"
              color={
                !isExcluded
                  ? theme.palette.primary.medium
                  : theme.palette.common.black
              }
              component="div"
              sx={{ minWidth: 76, flex: 0 }}
            >
              {capacityPerDay} per day
            </Typography>
          )}
        </Box>
        {steps.length > 0 &&
          steps.map((step) => (
            <Box key={step.id} className={classes.stepInfo}>
              <Typography
                variant="BD2"
                color={theme.palette.text.secondary}
                component="div"
              >
                {step.title}
              </Typography>
              <Typography variant="HD2" component="div">
                {formatDuration(stepDurations[step.id] ?? 0)}
              </Typography>
            </Box>
          ))}
      </Box>
      <MiButton
        variant={
          isExcluded ? BUTTON_VARIANTS.CONTAINED : BUTTON_VARIANTS.OUTLINED
        }
        color="primary"
        size="small"
        disabled={disabled}
        onClick={isExcluded ? onClickReenable : onClickExclude}
      >
        {isExcluded ? "Re-enable for Builder" : "Exclude from Builder"}
      </MiButton>
    </Box>
  );
};

const useStyles = makeStyles<{ isExcluded: boolean }>()(
  (theme, { isExcluded }) => ({
    root: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2),
      flexGrow: 1,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    },
    cardTitle: {
      opacity: isExcluded ? 0.6 : 1,
      height: 32,
      width: 250,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      "& > *": {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
      },
    },
    cardContent: {
      display: "flex",
      flex: 1,
      flexWrap: "wrap",
      gap: theme.spacing(8),
      opacity: isExcluded ? 0.4 : 1,
    },
    stepInfo: {
      display: "flex",
      flexDirection: "column",
      flex: "0 0 auto",
      width: 128,
      "& > *": {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
      },
    },
  })
);
